import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Search, ChevronDown, BookOpen, Utensils } from "lucide-react";
import BrandLockup from "@/components/BrandLockup";
import HeaderUtils from "@/components/HeaderUtils";
import PrimaryNav from "@/components/PrimaryNav";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { LEARN_CATEGORIES, LEARN_ITEMS } from "@/lib/learnContent";

const NEED_COLOR = { Essential: "#34C759", Helpful: "#4C9AFF", Optional: "#E2E30A" };

function Item({ it }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/10 bg-[#141414]" data-testid={`learn-item-${it.id}`}>
      <button type="button" data-testid={`learn-toggle-${it.id}`} onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-white/[0.03] transition-colors">
        <span className="min-w-0">
          <span className="text-sm font-semibold text-white block truncate">{it.term}</span>
          <span className="text-[11px] text-zinc-400">{it.simple}</span>
        </span>
        <span className="flex items-center gap-2 shrink-0">
          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-bold"
            style={{ color: "#0A0A0A", backgroundColor: NEED_COLOR[it.need] }}>{it.need}</span>
          <ChevronDown className={`h-4 w-4 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2 border-t border-white/5 pt-3">
          <div><p className="text-[10px] uppercase tracking-[0.2em] text-[#4C9AFF]">What it is</p><p className="text-sm text-zinc-200 mt-0.5">{it.what}</p></div>
          <div><p className="text-[10px] uppercase tracking-[0.2em] text-[#E2E30A]">Why it matters</p><p className="text-sm text-zinc-200 mt-0.5">{it.why}</p></div>
        </div>
      )}
    </div>
  );
}

export default function Learn() {
  const navigate = useNavigate();
  const { hasTracker, planTier } = useAuth();
  const isLite = planTier === "lite";
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");

  const items = useMemo(() => {
    const term = q.trim().toLowerCase();
    return LEARN_ITEMS.filter((it) => {
      if (cat !== "All" && it.category !== cat) return false;
      if (!term) return true;
      return (it.term + it.simple + it.what + it.why).toLowerCase().includes(term);
    });
  }, [q, cat]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="sticky top-0 z-40 bg-black/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <BrandLockup />
          <div className="ml-auto shrink-0"><HeaderUtils /></div>
        </div>
        <PrimaryNav />
      </header>

      <main data-testid="learn-page" className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-start gap-3 mb-6">
          <div className="grid place-items-center h-11 w-11 bg-[#E2E30A] shrink-0"><BookOpen className="h-5 w-5 text-black" /></div>
          <div>
            <h1 className="font-display text-2xl sm:text-3xl uppercase tracking-tight leading-none">The Knowledge Base</h1>
            <p className="text-sm text-zinc-300 mt-1.5">Straight answers on fuelling, macros, supplements and race day — plus everyday fuelling, female-athlete physiology, gut training, hydration and body composition. What it is, whether you actually need it, and where it lives inside the app.</p>
            <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mt-2" data-testid="learn-count">{LEARN_ITEMS.length} topics · {LEARN_CATEGORIES.length} categories</p>
          </div>
        </div>

        <div className="relative mb-3">
          <Search className="h-4 w-4 text-zinc-300 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="learn-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search e.g. creatine, gels, sodium…" className="pl-9 bg-black border-white/15 text-white" />
        </div>

        <div className="flex flex-wrap gap-1.5 mb-5">
          {["All", ...LEARN_CATEGORIES].map((c) => (
            <button key={c} data-testid={`learn-cat-${c}`} onClick={() => setCat(c)}
              className={`px-2.5 py-1 text-[11px] uppercase tracking-wider border transition-colors ${cat === c ? "border-[#E2E30A] text-[#E2E30A]" : "border-white/15 text-zinc-300 hover:text-white"}`}>{c}</button>
          ))}
        </div>

        <div className="space-y-2">
          {items.length === 0 ? (
            <p data-testid="learn-empty" className="text-sm text-zinc-400 py-8 text-center">Nothing matches that — try another word.</p>
          ) : items.map((it) => <Item key={it.id} it={it} />)}
        </div>

        <div className="mt-8 border border-[#4C9AFF]/40 bg-[#4C9AFF]/[0.07] p-4 flex items-start gap-3">
          <Utensils className="h-5 w-5 text-[#4C9AFF] shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-white">Put it into practice</p>
            <p className="text-xs text-zinc-300 mt-0.5">Your weekly plan already applies all of this to your training. {hasTracker ? "Use the Food Tracker to log and check your intake." : "Add the Food Tracker to log and check your intake against these targets."}</p>
            <div className="flex flex-wrap gap-2 mt-2">
              <Button data-testid="learn-cta" onClick={() => navigate("/tracker")}
                className="h-9 rounded-none bg-[#4C9AFF] hover:bg-[#3a86e6] text-black text-xs font-bold uppercase tracking-wide">
                {hasTracker ? "Open Food Tracker" : "Explore the Food Tracker"}
              </Button>
              {!isLite && (
                <Button data-testid="learn-recipes-cta" onClick={() => navigate("/recipes")}
                  className="h-9 rounded-none bg-transparent border border-[#FF9500]/50 text-[#FF9500] hover:bg-[#FF9500]/10 text-xs font-bold uppercase tracking-wide">
                  Browse Recipes
                </Button>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
